import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import { Button, media, styled, Loader, Dropdown } from '@payright/web-components';
import { ButtonCancelPlan } from '../../components/buttons/ButtonCancelPlan';
import SCSpinner from '../../components/spinner';
import { ROUTES } from '../../routes';
import { FeatureFlags, useFeatures } from '../../providers/features-provider';

const Section = styled.section`
  p {
    font-size: 1.13em;
    margin-bottom: 2rem;
  }
  .title {
    color: ${(props) => props.theme.colours.blue.base};
    font-weight: bold;
    font-size: 1.125rem;
  }

  ${media.min.medium} {
    .title {
      font-size: 1.5rem;
    }
  }

  .buttons {
    display: flex;
    gap: 1.5rem;
    flex-direction: column-reverse;
  }

  ${media.min.medium} {
    .buttons {
      flex-direction: row;
    }
  }

  .instructions-list {
    list-style-type: none;

    margin-bottom: 2rem;
    li {
      line-height: 1.5;
      margin-bottom: 2rem;
    }

    strong {
      font-weight: bold;
    }

    .dropdown {
      margin: 1rem 0;
    }

    a {
      color: ${(props) => props.theme.colours.blue.base};
      text-decoration: underline;
      cursor: pointer;
      &:hover {
        text-decoration: none;
      }
    }
  }

  .loading-wrapper {
    margin: 0;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const BANK_OPTIONS = [
  { name: 'AMEX', value: 'amex' },
  { name: 'ANZ', value: 'anz' },
  { name: 'Bank Australia', value: 'bank-australia' },
  { name: 'Bank of Melbourne', value: 'bank-of-melbourne' },
  { name: 'Bank of Queensland', value: 'bank-of-queensland' },
  { name: 'BankSA', value: 'banksa' },
  { name: 'Bankstown City Unity Bank', value: 'bcub' },
  { name: 'Bankwest', value: 'bankwest' },
  { name: 'Bendigo Bank', value: 'bendigo-bank' },
  { name: 'Beyond Bank', value: 'beyond-bank' },
  { name: 'Commonwealth Bank', value: 'commonwealth-bank' },
  { name: 'Credit Union SA', value: 'credit-union-sa' },
  { name: 'Great Southern Bank', value: 'great-southern-bank' }, // formerly known as CUA
  { name: 'Greater Bank', value: 'greater-bank' },
  { name: 'Heritage Bank', value: 'heritage-bank' },
  { name: 'ING', value: 'ing' },
  { name: 'Macquarie Bank', value: 'macquarie-bank' },
  { name: 'NAB', value: 'nab' },
  { name: 'Newcastle Permanent', value: 'newcastle-permanent' },
  { name: "People's Choice", value: 'peoples-choice' },
  { name: 'Queensland Country Bank', value: 'queensland-country-bank' },
  { name: 'Reliance Bank', value: 'reliance-bank' },
  { name: 'St.George', value: 'st-george' },
  { name: 'Suncorp Bank', value: 'suncorp-bank' },
  { name: 'Unity Bank', value: 'unity-bank' },
  { name: 'Westpac', value: 'westpac' },
];

export const BankStatementsPDFInstructionsContent = () => {
  const globalCheckoutState = useSelector((state: RootState) => state.checkout);
  const loading = globalCheckoutState.loading;

  const history = useHistory();
  const [selectedBank, setSelectedBank] = useState<string | undefined>(undefined);

  const {
    flagsConfig: { [FeatureFlags.PDF_BANK_STATEMENTS]: pdfBankStatementsFeatureFlag },
    isInitialised,
  } = useFeatures();

  // If the PDF statements feature flag is not switched on, redirect to the original bank statements IFRAME page
  // Wait for Flagsmith to initialize before checking the flag to avoid redirecting too early
  useEffect(() => {
    if (isInitialised && !pdfBankStatementsFeatureFlag) {
      history.push(ROUTES.PAYMENT_PLAN_BANK_STATEMENTS_ONLINE_BANKING + history.location.search);
    }
  }, [isInitialised, pdfBankStatementsFeatureFlag, history]);

  return (
    <Section>
      {!isInitialised ? (
        <div className="loading-wrapper">
          <Loader text="Loading..." />
        </div>
      ) : (
        <>
          <p className="title">How to upload your PDF bank statements</p>

          <p>
            Your privacy is our priority. We use secure systems to protect your data and only review
            what's necessary to assess your application.
          </p>

          <ol className="instructions-list">
            <li>
              <strong>Step 1: Check if your bank is accepted*</strong>
              <Dropdown
                options={BANK_OPTIONS}
                value={selectedBank}
                handleSelect={(value: string) => setSelectedBank(value)}
                placeholder="Select your bank"
              />
              <div>
                * Can't find your bank? Click{' '}
                <a
                  onClick={() =>
                    history.push(
                      ROUTES.PAYMENT_PLAN_BANK_STATEMENTS_ONLINE_BANKING + history.location.search
                    )
                  }
                >
                  here
                </a>{' '}
                for the instant verification option as your bank doesn't support this upload option.
              </div>
            </li>
            <li>
              <strong>Step 2: Log in to your bank's online portal or app</strong>
            </li>
            <li>
              <strong>
                Step 3: Download your PDF statements for the most recent 90 days, ending as close to
                today as possible
              </strong>{' '}
              (your most recent statements should be no older than 2 weeks old)
            </li>
            <li>
              <strong>
                Step 4: Check the PDFs show your full name, account number, and transaction details
              </strong>
            </li>
            <li>
              <strong>Step 5: Click Continue</strong> (you'll be prompted to upload your statements
              on the next page)
            </li>
          </ol>

          <div className="buttons">
            <ButtonCancelPlan disabled={loading} />
            <Button
              handleClick={() =>
                history.push(
                  ROUTES.PAYMENT_PLAN_BANK_STATEMENTS_PDF_UPLOAD + history.location.search
                )
              }
              disabled={loading}
              maxWidth="100%"
              icon={loading ? <SCSpinner /> : undefined}
            >
              Continue
            </Button>
          </div>
        </>
      )}
    </Section>
  );
};
